import { Component } from 'react';
import { Card, Button } from 'react-bootstrap';
import { GoX } from 'react-icons/go';
import Tooltip from "../../../controls/common/tooltip";
import { Converter } from "../../workspace/converter";
import SessionContext from "../../session/context";
import "./index.css";

export default class WidgetBase extends Component {
  static contextType = SessionContext;

  constructor(props) {
    super(props);
    this.subscribedAliases = new Set();
    this.discoveredAliases = new Set();
    this.cleanupFns = {};
  }

  loadObjectValue(component, route, type) {
    const alias = Converter.getComponentPortValueData(component, route);
    if ( !alias ) {
      /*
       * 2024-11-20: If value is not yet bound to a specific session alias
       * we just ignore it silently, instead of displaying an error.
       */
      return [null, null];
    }

    // Use context which is SessionManager
    const sessionManager = this.context;

    // Also support static usage if passed explicitly (backward compat or non-context usage)
    const manager = sessionManager || arguments[0];

    if ( !manager ) {
      return [null, "Session Manager is not available."];
    }

    if ( !manager.session ) {
      return [null, "Session is not selected."];
    }

    const value = manager.getSessionData(alias);

    // Register alias for subscription
    this.discoveredAliases.add(alias);

    if ( !value ) {
      return [null, `"${alias}" data is not available (not loaded yet or not present within selected session).`];
    }

    const valueType = value["type"];
    if ( type !== valueType ) {
      return [null, `Specified alias points at an object of "${valueType}" type. "${type}" object expected.`];
    }

    return [value, null];
  }

  componentDidMount() {
    this.updateSubscriptions();
    this.sessionListener = () => this.forceUpdate();
    if (this.context) {
      this.context.addListener(this.sessionListener);
    }
  }

  componentDidUpdate() {
    this.updateSubscriptions();
  }

  componentWillUnmount() {
    // Unsubscribe all
    for (const alias in this.cleanupFns) {
      this.cleanupFns[alias]();
    }
    this.cleanupFns = {};
    this.subscribedAliases.clear();

    if (this.context && this.sessionListener) {
      this.context.removeListener(this.sessionListener);
    }
    this.sessionListener = null;
  }

  updateSubscriptions() {
    const manager = this.context;
    if (!manager) return;

    // Unsubscribe from stale
    for (const alias of this.subscribedAliases) {
      if (!this.discoveredAliases.has(alias)) {
        if (this.cleanupFns[alias]) {
          this.cleanupFns[alias]();
          delete this.cleanupFns[alias];
        }
        this.subscribedAliases.delete(alias);
      }
    }

    // Subscribe to new
    for (const alias of this.discoveredAliases) {
      if (!this.subscribedAliases.has(alias)) {
        this.cleanupFns[alias] = manager.subscribe(alias, () => {
          this.forceUpdate();
        });
        this.subscribedAliases.add(alias);
      }
    }

    // Reset discovered for next render
    this.discoveredAliases.clear();
  }

  renderBody() {
    return (
      <Card.Body>
        Widget content implementation is missing.
      </Card.Body>
    );
  }

  getClassName() {
    return "";
  }

  getEffectiveSize() {
    if (this.props.data?.layoutType === "grid") {
      return { width: "100%", height: "100%", maxHeight: "100%" };
    }
    const w = Converter.getComponentPortValueData(this.props.data.component, "width");
    const h = Converter.getComponentPortValueData(this.props.data.component, "height");
    return { width: `${w}px`, height: `${h}px`, maxHeight: `${h}px` };
  }

  renderContent() {
    const { width, height, maxHeight } = this.getEffectiveSize();

    return (
      <Card.Body className="nodrag nowheel" style={{
        width,
        height,
        maxHeight,
        overflow: "scroll"
      }}>
        {this.renderBody()}
      </Card.Body>
    );
  }

  isSelected() {
    const shellInterface = this.props.data?.shellInterface;
    if (!shellInterface || !shellInterface.getState) return false;
    const state = shellInterface.getState();
    const componentId = this.props.data?.component?.id;
    return state?.details?.view === "component" && state?.details?.data?.id === componentId;
  }

  removeComponent = (event) => {
    event.stopPropagation();
    const shellInterface = this.props.data?.shellInterface;
    const componentId = this.props.data?.component?.id;
    if (!shellInterface || !shellInterface.getState || !componentId) return;
    const state = shellInterface.getState();
    const nextNodes = (state.nodes || []).filter((n) => n.id !== componentId);
    const workflowInterface = state.workflowInterface;
    if (workflowInterface && workflowInterface.setNodes) {
      workflowInterface.setNodes(nextNodes, /*isModified=*/true);
    }
    // Drop the connections that referenced the removed component (free-form only;
    // grid playgrounds don't carry connections, so the filter is a no-op).
    const nextEdges = (state.edges || []).filter(
      (e) => e.source !== componentId && e.target !== componentId
    );
    if (workflowInterface && workflowInterface.setEdges && nextEdges.length !== (state.edges || []).length) {
      workflowInterface.setEdges(nextEdges, /*isModified=*/true);
    }
    // Clear the details pane.
    if (shellInterface.updateDetails) {
      shellInterface.updateDetails(null, null);
    }
  };

  renderHeaderControls() {
    const canEdit = !!this.props.data?.shellInterface?.canEditRevision?.();
    return (
      <span className="playground-widget-controls float-right" onClick={(e) => e.stopPropagation()}>
        <span className="text-muted playground-widget-type">
          {this.props.data.component.type}
        </span>
        {canEdit && (
          <Tooltip text="Remove widget">
            <Button
              variant="light"
              size="sm"
              onClick={this.removeComponent}
            >
              <GoX />
            </Button>
          </Tooltip>
        )}
      </span>
    );
  }

  render() {
    // Clear discovered aliases at the beginning of render so subscription
    // accumulation starts fresh on each pass.
    this.discoveredAliases.clear();

    const isGrid = this.props.data?.layoutType === "grid";
    const isSelected = this.isSelected();
    const cardClassName = [
      "playground-widget",
      this.getClassName() || "",
      isGrid ? "playground-widget-grid" : "",
      isSelected ? "playground-widget-selected" : "",
    ].filter(Boolean).join(" ");

    // Header is only shown when the widget is selected (both modes), and
    // it overlays the content as a floating chip — see common/index.css.
    const renderHeader = isSelected;

    // Per-widget content zoom (grid mode only). Comes from the playground
    // shell's zoom slider — the layout itself stays at 1x so widget cells
    // remain aligned to the grid; only what's inside each cell scales.
    const widgetZoom = this.props.data?.zoom ?? 1;
    const contentStyle = isGrid ? { zoom: widgetZoom } : undefined;

    return (
      <Card className={cardClassName}>
        {renderHeader && (
          <Card.Header>
            <span className="playground-widget-name">
              {this.props.data.component.name}
            </span>
            {isSelected
              ? this.renderHeaderControls()
              : <span className="float-right text-muted">
                  {this.props.data.component.type}
                </span>}
          </Card.Header>
        )}
        <div className="playground-widget-content" style={contentStyle}>
          {this.renderContent()}
        </div>
      </Card>
    );
  }
}
